import { createSelector } from '@reduxjs/toolkit';
import canvases from 'domains/canvases';
import cropsModule from 'domains/crops';
import multiphotos from 'domains/multiphotos';
import photosModule from 'domains/photos';
import products from 'domains/products';
import activeEditor from 'domains/active_editor';
import { buildCartCropData } from 'utils/crop_data';
import { notNull, validCrop, validDimension } from 'utils/type_guards';
import { selectDefaultPPI } from 'selectors/ppi';
import { calculateBoundDimension } from 'utils/bounds_util';
import { orientationAndRatioPercentage } from 'utils/math_util';
import { fitImageToNode } from 'utils/pan_util';
import { ImageFilter } from 'types/photo_types';

/**
 * @deprecated
 */
export const selectCartPhotos = createSelector(
  [
    cropsModule.selectors.selectAll,
    photosModule.selectors.selectEntities,
    products.selectors.selectEntities,
    canvases.selectors.selectEntities,
  ],
  (crops, photosById, productsById, canvasesById) => {
    return crops
      .map((crop) => {
        if (!crop.photoLoaded) return null;

        const { canvasId, nodeUid } = cropsModule.utils.parseCropId(crop.id);
        const photo = photosById[crop.photoId];
        const productId = canvasesById[canvasId]?.product;

        if (!photo || !productId || !validCrop(crop)) return null;

        // Find the real id for this crop's node uid
        const node = productsById[productId]?.nodes.find((n) => n.uid === nodeUid);
        if (!node) return null;

        const cartCrop = buildCartCropData(crop, photo, node.id);

        // - Include the canvasId to identify the correct cart photos in add to cart
        // - Reused for arrangement crops, so include the nodeUid for restoring the correct crop
        return { canvasId, nodeUid, cartCrop };
      })
      .filter(notNull);
  }
);

/**
 * @deprecated
 */
export const selectMultiPhotoCartPhotos = createSelector(
  [
    multiphotos.selectors.selectAll,
    photosModule.selectors.selectEntities,
    products.selectors.selectEntities,
    activeEditor.selectors.canvas,
    selectDefaultPPI,
  ],
  (multiphotos, photosById, productsById, activeCanvas, ppi) => {
    const withImageDimensions = multiphotos.filter((mp) => mp.photoLoaded);

    if (!activeCanvas || !activeCanvas.product || multiphotos.length !== withImageDimensions.length)
      return [];

    const product = productsById[activeCanvas.product];
    const node = product?.nodes.find((n) => n.node_type === 1);
    if (!product || !node) return [];

    const isCustomProduct = products.utils.isCustomSizeProduct(product);

    const [, , w, h] = node.bounds
      .split(',')
      .map((b) => calculateBoundDimension(b, 'image', product, activeCanvas) * ppi);

    const { orientation: nodeOrientation } = orientationAndRatioPercentage(w, h);

    return multiphotos
      .map((mp) => {
        const photo = photosById[mp.id];
        const imageDimensions = { height: mp.imageHeight, width: mp.imageWidth };

        if (!validDimension(imageDimensions)) return null;

        const { orientation: imageOrientation } = orientationAndRatioPercentage(
          imageDimensions.width,
          imageDimensions.height
        );

        const autoRotate =
          imageOrientation !== 'square' &&
          imageOrientation !== nodeOrientation &&
          product.rotation_allowed &&
          nodeOrientation !== 'square' &&
          !isCustomProduct;

        const nodeDimensions = autoRotate ? { width: h, height: w } : { width: w, height: h };

        const { fitCropH, fitCropW } = fitImageToNode(nodeDimensions, imageDimensions);

        const crop = {
          originalW: imageDimensions.width,
          originalH: imageDimensions.height,
          fitCropH,
          fitCropW,
          cropH: fitCropH,
          cropW: fitCropW,
          cropY: 0.5,
          cropX: 0.5,
          filter: 'original' as ImageFilter['filterType'],
          rotation: 0,
          zoom: 1,
        };

        if (!photo || !validCrop(crop)) return null;

        const cartCrop = buildCartCropData(crop, photo, node.id);

        return cartCrop;
      })
      .filter(notNull);
  }
);
